import { Component, ChangeDetectionStrategy, NgZone, Input, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { chart, Options, XAxisPlotLinesOptions } from 'highcharts';

import moment from 'moment';

import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
HighchartsMore(Highcharts);
import HighchartsXrange from 'highcharts/modules/xrange';
import {
    calculateDurationAndZoomLevel,
    TimelineZoomLevel,
} from 'locations/station-dialog/status/timeline-controls/timeline-controls.component';
import { TimeDisplay, TimeDisplayService } from 'shared/time-display-service/time-display.service';
import { distinctUntilKeyChanged, Subject, takeUntil } from 'rxjs';
import { AdjustUtcDatePipe } from 'shared/pipes/date/adjust-utc-date/adjust-utc-date.pipe';
import { UntilDestroy } from '@ngneat/until-destroy';
HighchartsXrange(Highcharts);

let GraphId = 0;

const powerColor = '#81a9c9';

const baseOptions: () => Options = () => ({
    chart: {
        styledMode: true,
    },
    time: {
        timezoneOffset: new Date().getTimezoneOffset(),
        useUTC: false,
    },
    yAxis: {
        title: { text: null },
        labels: {
            format: '{value} kW',
            style: {
                color: powerColor,
                padding: '8px',
            },
            useHTML: true,
        },
        min: 0,
        softMax: 40,
        offset: -16,
        maxPadding: 0,
        minPadding: 0,
        tickInterval: 10,
    },
    xAxis: {
        title: { text: null },
        type: 'datetime',
        labels: {
            enabled: true,
        },
        showLastLabel: true,
        endOnTick: false,
        opposite: true,
        crosshair: {
            width: 1,
            snap: false,
        },
        tickInterval: undefined,
    },
    legend: {
        enabled: false,
    },
    tooltip: {
        shared: true,
        shadow: false,
        borderWidth: 0,
        backgroundColor: 'transparent',
        padding: 0,
        positioner: function (w, h) {
            return {
                x: this.chart.plotLeft,
                y: this.chart.chartHeight - h,
            };
        },
    },
    plotOptions: {
        series: {
            animation: false,
        },
        backgroundColor: 'var(--white-color)',
    },
    credits: { enabled: false },
    title: null,
});

@Component({
    selector: 'ce-total-power-graph',
    templateUrl: './total-power-graph.component.html',
    styleUrls: ['./total-power-graph.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class TotalPowerGraphComponent {
    @Input() samples: [number, number, number][];
    @Input() reservationSamples: [number, number, number][];
    @Input() startTime: string;
    @Output() startTimeChange = new EventEmitter<string>();
    @Input() endTime: string;
    @Output() endTimeChange = new EventEmitter<string>();
    @Input() seekTime: string;
    @Output() seekTimeChange = new EventEmitter<string>();
    @Input() zoomLevel: TimelineZoomLevel;
    @Output() zoomLevelChange = new EventEmitter<TimelineZoomLevel>();

    graphId = `total-power-graph-${++GraphId}`;
    desiredHeightPx = 124;

    chart: Highcharts.Chart;

    currentTime: TimeDisplay;
    onDestroy$ = new Subject<void>();

    constructor(
        private ngZone: NgZone,
        private timeDisplayService: TimeDisplayService,
        private adjustUtcDatePipe: AdjustUtcDatePipe
    ) {
        this.timeDisplayService.timeObservable$
            .pipe(takeUntil(this.onDestroy$), distinctUntilKeyChanged('utcLabel'))
            .subscribe((time) => {
                this.currentTime = time;

                if (this.chart) {
                    this.redraw();
                }
            });
    }

    ngAfterViewInit() {
        this.init();
        this.updatePlotlines();
        this.updateTickInterval();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            this.chart &&
            (changes.samples || changes.reservationSamples || changes.startTime || changes.endTime) &&
            this.samples
        ) {
            this.redraw();
        } else if (this.chart && (changes.seekTime || changes.zoomLevel)) {
            this.updatePlotlines();
            this.updateTickInterval();
        }
    }

    redraw() {
        this.update();
        this.updatePlotlines();
        this.updateTickInterval();
        this.chart.reflow();
    }

    updatePlotlines() {
        this.ngZone.runOutsideAngular(() => {
            // draw plot lines at the end of each day
            const dateLines: XAxisPlotLinesOptions[] = [];
            const date = moment(this.endTime).endOf('day');
            const earliestDate = moment(this.startTime).startOf('day');

            while (date.toISOString() >= earliestDate.toISOString()) {
                dateLines.push({
                    value: +date.toDate(), // display as number
                    width: 1,
                    color: '#808080',
                    zIndex: 11,
                });
                date.subtract(1, 'days');
            }
            // start of earliest date
            dateLines.push({
                value: +earliestDate.toDate(), // display as number
                width: 1,
                color: '#808080',
                zIndex: 11,
            });

            // current seekTime
            if (this.seekTime) {
                dateLines.push({
                    value: Date.parse(this.seekTime),
                    width: 1,
                    color: '#FF0000',
                    zIndex: 13,
                });
            }

            this.chart.update({
                xAxis: {
                    plotLines: dateLines,
                    plotBands: [
                        {
                            from: Date.now(),
                            to: Date.parse(this.endTime),
                            className: 'future-plot-band',
                            zIndex: 10,
                        },
                    ],
                },
            });
        });
    }

    updateTickInterval() {
        this.ngZone.runOutsideAngular(() => {
            let tickInterval: number = undefined; // let highcharts calculates interval
            const HOUR_IN_MINUTE = 1 * 60;
            const { duration, zoomLevel } = calculateDurationAndZoomLevel(this.startTime, this.endTime);

            if (zoomLevel === 'HOUR') {
                tickInterval = 2 * 1000 * 60; // 2 minute interval
            } else if (zoomLevel === 'CUSTOM' && duration <= HOUR_IN_MINUTE) {
                tickInterval = 1 * 1000 * 60; // 1 minute interval
            } else {
                tickInterval = undefined;
            }

            // xAxis
            this.chart.options.xAxis[0].tickInterval = tickInterval;
        });
    }

    init() {
        const options = this.getOptions();
        this.ngZone.runOutsideAngular(() => {
            this.chart = chart(this.graphId, options);
        });
    }

    update() {
        const options = this.getOptions();
        this.ngZone.runOutsideAngular(() => {
            this.chart.update(options);
        });
    }

    getOptions() {
        const _options = baseOptions();
        _options.time.timezoneOffset = this.currentTime.utcTimezoneOffset ?? new Date().getTimezoneOffset();
        // todo: fix
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        const formatDateFunction = this.formatDateToUtc.bind(this);

        const options: Options = {
            ..._options,
            chart: {
                height: this.desiredHeightPx,
                spacingLeft: 0,
                zooming: {
                    type: 'x',
                },
                events: {
                    click: function (ev) {
                        const x = new Date(this.xAxis[0].toValue(ev.chartX)).toISOString();
                        self.ngZone.run(() => {
                            self.seekTimeChange.emit(x);
                        });
                    },
                    selection: function (ev) {
                        if (ev.xAxis) {
                            self.ngZone.run(() => {
                                ev.originalEvent.preventDefault(); // prevent default zooming action, send new values to redraw chart, hide reset-zoom button

                                const startTime = moment(ev.xAxis[0].min).toISOString();
                                const endTime = moment(ev.xAxis[0].max).toISOString();

                                const { zoomLevel } = calculateDurationAndZoomLevel(startTime, endTime);

                                self.seekTimeChange.emit(startTime);
                                self.startTimeChange.emit(startTime);
                                self.endTimeChange.emit(endTime);
                                self.zoomLevelChange.emit(zoomLevel);
                            });
                        }
                        return false;
                    },
                },
            },
            tooltip: {
                ..._options.tooltip,
                useHTML: true,
                formatter: function () {
                    const powerkW = Math.round(this.y);
                    const date = formatDateFunction(moment(this.x).toISOString());
                    return `<div style="color: var(--primary-black-color);">
                        ${date} – peak output <b>${powerkW}</b> kW
                    </div>`;
                },
            },
            xAxis: {
                ..._options.xAxis,
                min: this.startTime ? Date.parse(this.startTime) : undefined,
                max: this.endTime ? Date.parse(this.endTime) : undefined,
                tickInterval: undefined,
            },
            series: [
                {
                    type: 'areaspline',
                    name: 'Charging Power',
                    marker: { enabled: true, radius: 2 },
                    data: this.samples,
                    tooltip: {
                        valueSuffix: 'kW',
                    },
                    states: {
                        hover: {
                            lineWidth: 2,
                        },
                    },
                    lineWidth: 2,
                    color: document.documentElement.classList.contains('dark') ? '#4f9ad2' : '#86b5d5',
                    fillColor: document.documentElement.classList.contains('dark') ? '#1a3a4d' : '#e9f0f6',
                    zIndex: -1,
                },
                ...(this.reservationSamples
                    ? [
                          {
                              type: 'area',
                              name: 'Power reservation',
                              marker: { enabled: false },
                              data: this.reservationSamples,
                              tooltip: {
                                  valueSuffix: 'kW',
                              },
                              color: document.documentElement.classList.contains('dark') ? '#666666' : '#d0d0d0',
                              lineWidth: 1,
                              states: {
                                  hover: {
                                      lineWidth: 0,
                                  },
                                  inactive: {
                                      opacity: 1,
                                  },
                              },
                              zIndex: -2,
                              enableMouseTracking: false,
                          } as Highcharts.SeriesAreaOptions,
                      ]
                    : []),
            ],
        };

        return options;
    }

    private formatDateToUtc(date: string, format = 'DD.MM.yyyy HH:mm') {
        return this.adjustUtcDatePipe.transform(date, { time: this.currentTime, format: format ?? null });
    }
}
